import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSession } from "@/App";
import { AssessmentBreadcrumb } from "@/components/AssessmentBreadcrumb";
import { ScenarioCompleteDialog, FinalConfirmationDialog, PathChangeWarningDialog } from "@/components/AssessmentDialogs";
import { StageOptionsList, ReviewModeNotice, AssessmentNavigation } from "@/components/AssessmentControls";
import { useAssessmentFlow } from "@/hooks/useAssessmentFlow";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AssessmentPage() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { session: user, updateSession: updateUser } = useSession();

  const flow = useAssessmentFlow({ assessmentId, apiBase: API, user, updateUser, navigate });

  if (!flow.stageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  const atStart = flow.currentScenarioIndex === 0 && flow.currentStage === 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6 fade-in">
        <AssessmentBreadcrumb
          scenarios={flow.scenarios}
          currentScenarioIndex={flow.currentScenarioIndex}
          currentStage={flow.currentStage}
          totalScenarios={flow.totalScenarios}
        />

        <Card className="shadow-2xl border-0 backdrop-blur-sm bg-white/95 slide-up">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed prose prose-lg max-w-none">
              {flow.stageData.stem}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6">
            <StageOptionsList
              shuffledOptions={flow.shuffledOptions}
              selectedOption={flow.selectedOption}
              onSelect={flow.handleOptionSelect}
            />

            {flow.isReviewMode && (
              <ReviewModeNotice
                hasExistingChoice={flow.hasExistingChoice}
                currentStage={flow.currentStage}
              />
            )}

            <AssessmentNavigation
              canGoBack={!atStart}
              canSkipToEnd={flow.canSkipToEnd}
              canSubmit={!!flow.selectedOption}
              submitting={flow.submitting}
              isLastQuestion={flow.isLastQuestion}
              onBack={flow.handleBack}
              onSkipToEnd={flow.handleSkipToEnd}
              onNext={flow.handleNext}
            />
          </CardContent>
        </Card>
      </div>

      <ScenarioCompleteDialog
        open={flow.showScenarioComplete}
        onOpenChange={flow.setShowScenarioComplete}
        scenarioNumber={flow.currentScenarioIndex + 1}
        onBack={flow.handleBack}
        onContinue={flow.handleContinueToNextScenario}
      />

      <FinalConfirmationDialog
        open={flow.showFinalConfirmation}
        onOpenChange={flow.setShowFinalConfirmation}
        onReview={flow.handleReviewResponses}
        onSubmit={flow.handleFinalSubmit}
      />

      <PathChangeWarningDialog
        open={flow.showPathChangeWarning}
        onOpenChange={flow.setShowPathChangeWarning}
        currentStage={flow.currentStage}
        onCancel={flow.handleCancelPathChange}
        onConfirm={flow.handleConfirmPathChange}
      />
    </div>
  );
}
